import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useCallback, useState } from "react";
import { FormContainer, FormInput, Treturn, useFetch } from "@4uhub/lib4uhub";
import { useParams } from "react-router-dom";
import { SwitchInput } from "../../../../components/UI/Inputs/Switch";
import ExtensionSettingsService from "../../../../services/extensionSettings.service";
import {
  TExtensionSettingForm,
  extensionSettingSchema,
} from "./ExtensionSettingSchema";
import { Information } from "../../../../components/Information/Information";

const translationPath = "page.extensionSettings.";

const extensionSettingsService = new ExtensionSettingsService();

const ExtensionSetting = () => {
  const { t } = useTranslation();
  const [saveConfig, setSaveConfig] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [siteAppName, setSiteAppName] = useState<string>();
  const [extensionName, setExtensionName] = useState<string>();
  const urlParams = useParams();

  const { sendRequest: save, loading: sLoading } = useFetch(
    extensionSettingsService.create,
  );
  const { sendRequest: update, loading: uLoading } = useFetch(
    extensionSettingsService.updateItemById,
  );
  const { sendRequest: get, loading: gLoading } = useFetch(
    extensionSettingsService.getItemById,
  );

  const saveHandler = (v: TExtensionSettingForm) =>
    save({
      ...v,
      identifier: v.identifier,
      property: v.property,
      value: v.value,
      description: v.description,
      siteAppId: !saveConfig ? urlParams.siteAppId : undefined,
      extensionId: urlParams.extensionId || "",
    });

  const updateHandler = (v: TExtensionSettingForm, id: string) =>
    update({
      item: {
        ...v,
        identifier: v.identifier,
        property: v.property,
        description: v.description,
        siteAppId: !saveConfig ? urlParams.siteAppId : undefined,
        value: v.value,
        extensionId: urlParams.extensionId || "",
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TExtensionSettingForm>> => {
      const { data, success } = await get(id);
      if (!data) {
        return { data, success };
      }

      setIsEnabled(data.enable);

      if (!data.siteApp) {
        setSaveConfig(true);
      } else {
        setSiteAppName(data.siteApp.name);
        setExtensionName(data.extension.name);
        setSaveConfig(false);
      }

      return {
        data: {
          ...data,
          id: data.id,
          identifier: data.identifier,
          property: data.property,
          value: data.value,
          description: data.description,
        },
        success: true,
      };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TExtensionSettingForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "extension_config")}
      schema={extensionSettingSchema}
      subtitle={t(translationPath + "new_extension_config")}
      subtitleWatchField={"identifier"}
      fields={() => (
        <Grid container spacing={2}>
          {siteAppName && (
            <Grid item xs={12} sm={8} mb={2}>
              <Typography fontSize={18}>
                {t(translationPath + "extension_settings", {
                  siteApp: siteAppName,
                  extension: extensionName,
                })}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12} sm={siteAppName ? 4 : 12} textAlign={"end"}>
            <FormControlLabel
              checked={isEnabled}
              control={
                <Switch onChange={(e) => setIsEnabled(e.target.checked)} />
              }
              label={
                isEnabled
                  ? t(translationPath + "switchEnableDisableConfig.enabled")
                  : t(translationPath + "switchEnableDisableConfig.disabled")
              }
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <SwitchInput
                name="isPublic"
                label={t(translationPath + "is_public")}
                defaultValue={false}
              />

              <Information
                title={t(translationPath + "is_public_info")}
                iconSize="small"
                iconColor="action"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="identifier"
              fullWidth
              label={t(translationPath + "identifier") || "Identifier"}
              autoFocus
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="property"
              fullWidth
              label={t(translationPath + "property") || "Property"}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormInput
              size="small"
              name="value"
              label={t(translationPath + "value") || "Value"}
              fullWidth
              multiline
              minRows={4}
            />
          </Grid>

          <Grid item xs={12}>
            <FormInput
              size="small"
              multiline
              name="description"
              fullWidth
              minRows={4}
              label={t("page.register.generics.description") || "Description"}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              checked={saveConfig}
              control={<Checkbox />}
              onChange={(_, checked) => setSaveConfig(checked)}
              label={
                t(translationPath + "save_in_establishment") ||
                "Salvar configurações para todos os sites e apps do estabelecimento"
              }
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default ExtensionSetting;
